import React, { useMemo } from "react";
import Select, { FormatOptionLabelMeta } from "react-select";

import FallbackImage from "components/global/FallbackImage";
import {
  getSchoolSelectorStyles,
  SchoolSelectOption,
} from "components/global/select-field-styles";
import {
  SchoolSelectorBrandLogo,
  SchoolSelectorControl,
  SchoolSelectorEmptyMessage,
  SchoolSelectorField,
  SchoolSelectorLogoSlot,
  SchoolSelectorOptionLabel,
  SchoolSelectorOptionLogo,
  SchoolSelectorSelectWrap,
} from "components/global/styles/SchoolSelector.inline.styles";
import { useScopedSchoolSelection } from "hooks/use-scoped-school-selection";
import { PortalAccessProfile } from "types/api";

const ATHSPIRE_LOGO_SRC = "/assets/images/brand/athspire-logo.svg";

const getSchoolImageSrc = (school: { image?: string | null; image_url?: string | null }) =>
  school.image_url || school.image || null;

const formatSchoolOptionLabel = (
  option: SchoolSelectOption,
  { context }: FormatOptionLabelMeta<SchoolSelectOption>,
) => {
  if (context === "value") {
    return option.label;
  }

  return (
    <SchoolSelectorOptionLabel>
      <SchoolSelectorOptionLogo>
        <FallbackImage
          alt=""
          className="h-7 w-7 rounded-lg"
          fallbackLabel={option.label}
          src={option.imageSrc}
          variant="school"
        />
      </SchoolSelectorOptionLogo>
      <span>{option.label}</span>
    </SchoolSelectorOptionLabel>
  );
};

type SchoolSelectorProps = {
  onSchoolChange?: (nextSchoolId: string) => void;
  portalAccess: PortalAccessProfile | null;
};

export const SchoolSelector: React.FC<SchoolSelectorProps> = ({
  onSchoolChange,
  portalAccess,
}) => {
  const { handleSchoolChange, schoolId, schools, schoolsQuery, selectedSchool } =
    useScopedSchoolSelection({
      manageActiveSchoolId: true,
      portalAccess,
    });

  const schoolOptions = useMemo<SchoolSelectOption[]>(
    () =>
      schools.map((school: any) => ({
        imageSrc: getSchoolImageSrc(school),
        label: school.name,
        value: String(school.id),
      })),
    [schools],
  );

  const selectedOption = useMemo(
    () => schoolOptions.find((option) => option.value === String(schoolId)) || null,
    [schoolId, schoolOptions],
  );

  const selectStyles = useMemo(() => getSchoolSelectorStyles(), []);
  const showEmptyState = !schoolsQuery.isLoading && schools.length === 0;
  const isSingleSchool = schoolOptions.length === 1;
  const isSelectorDisabled = schoolsQuery.isLoading || isSingleSchool;

  if (showEmptyState) {
    return <SchoolSelectorEmptyMessage>No schools available</SchoolSelectorEmptyMessage>;
  }

  return (
    <SchoolSelectorField>
      <SchoolSelectorControl>
        <SchoolSelectorLogoSlot aria-hidden="true">
          {selectedSchool ? (
            <FallbackImage
              alt=""
              className="h-9 w-9 rounded-xl"
              fallbackLabel={selectedSchool.name}
              src={getSchoolImageSrc(selectedSchool)}
              variant="school"
            />
          ) : (
            <SchoolSelectorBrandLogo alt="" src={ATHSPIRE_LOGO_SRC} />
          )}
        </SchoolSelectorLogoSlot>

        <SchoolSelectorSelectWrap>
          <Select
            aria-label="Select school"
            classNamePrefix="school-selector"
            formatOptionLabel={formatSchoolOptionLabel}
            inputId="school-selector"
            isClearable={false}
            isDisabled={isSelectorDisabled}
            isSearchable={schoolOptions.length > 5}
            menuPlacement="auto"
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            onChange={(option) => {
              const nextSchoolId = option?.value ?? "";
              handleSchoolChange(nextSchoolId);
              onSchoolChange?.(nextSchoolId);
            }}
            options={schoolOptions}
            placeholder={schoolsQuery.isLoading ? "Loading schools..." : "Select a school"}
            styles={selectStyles}
            value={selectedOption}
          />
        </SchoolSelectorSelectWrap>
      </SchoolSelectorControl>
    </SchoolSelectorField>
  );
};
